<template>
  <div :class="$style.sidebar">
    <div :class="$style.sidebarTop">
      <div v-if="showMobileLine" :class="$style.mobileLine" @click="handleMobileLineClick" />
      <aw-simple-search-input
        v-model:search-text="searchText"
        :placeholder="'Keresés'"
        :is-small="false"
        :is-search-btn-showed="false"
        :class="$style.searchInput"
        sr-only-label="aw.map.sidebar.search_sr_label"
      />
      <div v-if="availableCouriers.length > 1" :class="$style.checkboxes">
        <aw-checkbox-version2
          v-for="courier in checkboxes"
          :key="courier.courier"
          v-model="courier.checked"
          :has-label="true"
          :label="courierPromotionals.has(courier.courier) ? undefined : $awt(`aw.map.courier.${courier.courier}`)"
          @update:model-value="handleCheckboxChange(courier, $event)"
        >
          <template v-if="courierPromotionals.has(courier.courier)" #label>
            <span :class="$style.courierLabel">
              <span v-text="$awt(`aw.map.courier.${courier.courier}`)" />
              <lv-icon name="pig-16" :size="16" :class="$style.promotionalIcon" />
            </span>
          </template>
        </aw-checkbox-version2>
      </div>
    </div>
    <div v-if="courierAdditionalTexts.length" :class="$style.additionalTextInfoBoxes">
      <aw-notification-item
        v-for="item in courierAdditionalTexts"
        :key="item.courier"
        is-margin-less
        index="0"
        :notification="{
          type: 'info',
          iconName: 'information-circle-16',
          manualClose: false,
          text: {
            title: $awt(`aw.map.courier.${item.courier}`),
            subtitle: item.text,
          },
        }"
      />
    </div>
    <div
      v-if="searchedPoints.length"
      ref="scrollElementRef"
      :class="[
        $style.pointWrapper,
        $style[`pointWrapper${currentWidth}`],
        {
          [$style.pointWrapperScrollable]: onlyPointsScroll,
          [$style.pointWrapperBig]: availableCouriers.length < 2,
        },
      ]"
    >
      <div
        v-for="(message, dmId) in deliveryMethodMessages"
        :key="dmId"
        :class="$style.deliveryMethodMessages"
      >
        <aw-notification-item
          is-margin-less
          index="0"
          :notification="{
            type: 'warning',
            iconName: 'maintenence-16',
            manualClose: false,
            text: {
              title: $awt('aw.map.sidebar.delivery_method_message.warning.title'),
              subtitle: message,
            },
          }"
        />
      </div>
      <ul :class="$style.pointList">
        <li
          v-for="point in searchedPoints"
          :key="point.id"
          :class="[$style.point, {[$style.pointOpened]: openedPoint?.id === point.id, [$style.pointSelected]:selectedPoint?.id === point.id}]"
          @click="selectPoint(point)"
        >
          <div :class="$style.pointInfo">
            <component :is="headingTag" :class="$style.pointTitle" :title="point.name">
              <span
                v-if="selectedPoint?.id === point.id"
                class="awSrOnlyAbs"
                v-text="$awt('aw.map.sidebar.point_selected')"
              />
              <span>
                {{ point.name }}
              </span>
            </component>
            <span :class="$style.pointAddress" :title="formatAddress(point.address)">
              {{ formatAddress(point.address) }}
            </span>
            <button
              type="button"
              class="awSrOnlyAbs"
              @click="selectPoint(point)"
              v-text="$awt('aw.map.sidebar.select_point_btn')"
            />
            <button
              type="button"
              :class="[$style.pointExpand, 'buttonReset']"
              :aria-controls="`openedPointInformation-${point.id}`"
              :aria-expanded="openedPoint?.id === point.id"
              :aria-label="$awt(`aw.map.sidebar.details_toggle.${(openedPoint?.id === point.id) ? 'close' : 'open'}`)"
              :title="$awt(`aw.map.sidebar.details_toggle.${(openedPoint?.id === point.id) ? 'close' : 'open'}`)"
              @click="togglePoint(point, $event)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  :d="(openedPoint?.id === point.id
                    ? 'M11.7881 8H4.21194M13.3033 2.6967C16.2322 5.62563 16.2322 10.3744 13.3033 13.3033C10.3744 16.2322 5.62563 16.2322 2.6967 13.3033C-0.232234 10.3744 -0.232234 5.62563 2.6967 2.6967C5.62563 -0.232234 10.3744 -0.232234 13.3033 2.6967Z'
                    : 'M8.00001 11.7881L8.00001 4.21193M11.7881 8L4.21194 8M13.3033 2.6967C16.2322 5.62563 16.2322 10.3744 13.3033 13.3033C10.3744 16.2322 5.62563 16.2322 2.6967 13.3033C-0.232233 10.3744 -0.232233 5.62563 2.6967 2.6967C5.62563 -0.232233 10.3744 -0.232233 13.3033 2.6967Z'
                  )"
                  stroke="black"
                  style="stroke: currentColor;"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <img
            :src="getLogo(selectedPoint?.id, point)"
            alt=""
            :class="$style.pointLogo"
            width="28"
            height="28"
            loading="lazy"
          >
          <div :class="$style.pointPrice">
            <aw-delivery-price-badge
              :price="point.price"
              :promotional="point.promotional"
              :show-from="false"
            />
          </div>
          <div
            v-show="openedPoint?.id === point.id"
            :id="`openedPointInformation-${point.id}`"
            :class="$style.openedPointInformation"
          >
            <template v-if="openedPoint?.id === point.id">
              <aw-separator margin-top="none" margin-bottom="none" />
              <component :is="headingTag1" :class="$style.informationLabel">
                {{ $awt('aw.map.sidebar.opening_hours_label') }}
              </component>
              <ul :class="['ulReset', $style.informationList]">
                <li
                  v-for="openingHour in pointData[point.id]"
                  :key="openingHour.dayOfWeek"
                  :class="$style.informationHour"
                  :aria-label="`${dayOfWeekLabel[openingHour.dayOfWeek]} ${openingHour.openingHours || $awt('openings.closed')}.`"
                >
                  <span>{{ dayOfWeekLabel[openingHour.dayOfWeek] }}</span>
                  <span>{{ openingHour.openingHours || $awt('openings.closed') }}</span>
                </li>
              </ul>
              <aw-separator margin-top="none" margin-bottom="none" />
              <div>
                <component :is="headingTag1" :class="$style.informationLabel">
                  {{ $awt('aw.map.sidebar.phone_number') }}
                </component>
                <p v-text="point.phoneNumber || '-'" />
              </div>
              <template v-if="point.paymentMethods?.length">
                <aw-separator margin-top="none" margin-bottom="none" />
                <component :is="headingTag1" :class="$style.informationLabel">
                  {{ $awt('aw.map.sidebar.payment_options') }}
                </component>
                <ul :class="['ulReset', $style.informationList]">
                  <li
                    v-for="paymentOptionString in point.paymentMethods"
                    :key="paymentOptionString"
                    v-text="paymentOptionString"
                  />
                </ul>
              </template>
            </template>
          </div>
        </li>
      </ul>
    </div>

    <div v-else-if="searchText" :class="$style.sidebarEmpty">
      <div v-html="$awt('aw.map.sidebar.no_results_found')" />
      <img
        :src="$addWebpPostfixIfSupported({ url: abstract77, webpUrl: abstract77Webp })"
        alt=""
        :class="$style.emptyImage"
        loading="lazy"
      >
    </div>

    <div v-else :class="$style.spinnerContainer">
      <aw-spinner :class="$style.spinner" />
    </div>
  </div>
</template>
<script setup>
  import { useNuxtApp } from 'nuxt/app';
  import { defineAsyncComponent, inject, ref, useTemplateRef, watch } from 'vue';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import { calculateHeadingChildren } from '~~/common/utils/headingTag';
  import { formatAddress } from '~~/common/utils/addressFormatter';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import AwSimpleSearchInput from '~~/common/components/Common/AwSimpleSearchInput.vue';
  import AwCheckboxVersion2 from '~~/common/components/Common/Widgets/AwCheckboxVersion2.vue';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  import { useToasterStore } from '~~/common/stores/toaster.js';
  import { useMapLogos } from '~~/common/utils/mapLogo';
  import abstract77 from '~~/common/assets/images/abstract-77.png';
  import abstract77Webp from '~~/common/assets/images/abstract-77.png.webp';
  import { LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import AwDeliveryPriceBadge from '~~/shop/app/components/Page/Checkout/AwDeliveryPriceBadge.vue';

  const AwNotificationItem = defineAsyncComponent(() => import('~~/common/components/Common/AwNotificationItem.vue'));

  const {
    $awt,
    $api,
    $logger,
  } = useNuxtApp();

  const emit = defineEmits(['select-point', 'search-text-changed', 'courier-checked-change', 'mobile-line-click']);
  const currentCheckoutType = inject('currentCheckoutType');

  const props = defineProps({
    selectedPoint: {
      type: Object,
      default: null,
    },
    onlyPointsScroll: {
      type: Boolean,
      required: true,
    },
    currentWidth: {
      type: String,
      required: true,
    },
    availableCouriers: {
      type: Array,
      required: true,
    },
    searchedPoints: {
      type: Array,
      required: true,
    },
    showMobileLine: {
      type: Boolean,
      default: false,
    },
    headingTag: {
      type: String,
      required: true,
      default: 'h3',
    },
    courierAdditionalTexts: {
      type: Array,
      default: () => [],
    },
    courierPromotionals: {
      type: Set,
      default: () => new Set(),
    },
  });

  const toasterStore = useToasterStore();
  const scrollElementRef = useTemplateRef('scrollElementRef');

  const openedPoint = ref(null);
  const pointData = ref({});
  const searchText = ref('');
  const checkboxes = ref(props.availableCouriers.map((courier) => ({
    checked: true,
    courier,
  })));

  const dayOfWeekLabel = {
    0: $awt('days.monday'),
    1: $awt('days.tuesday'),
    2: $awt('days.wednesday'),
    3: $awt('days.thursday'),
    4: $awt('days.friday'),
    5: $awt('days.saturday'),
    6: $awt('days.sunday'),
  };

  const { getLogo } = useMapLogos();

  const hasCheckedCheckboxes = computed(() => checkboxes.value.some((courier) => courier.checked));

  const headingTag1 = computed(() => {
    return calculateHeadingChildren(props.headingTag);
  });

  const deliveryMethodMessages = computed(() => {
    const checkoutStore = useCheckoutStore();
    const uniqueness = new Set();
    return checkoutStore.availableDeliveryMethods.reduce((acc, dm) => {
      const message = dm?.lockerInfoMessage || '';
      const isAlreadyInList = uniqueness.has(message);
      uniqueness.add(message);
      if (typeof message === 'string' && message && !isAlreadyInList) {
        acc[dm.id] = message;
      }
      return acc;
    }, {});
  });

  function selectPoint (point) {
    emit('select-point', point);
  }

  watch(() => props.selectedPoint, (newVal, oldVal) => {
    if (newVal?.id !== oldVal?.id) {
      if (scrollElementRef.value) {
        scrollElementRef.value.scrollTop = 0;
      }
    }
  });

  async function togglePoint (point, event) {
    event.stopPropagation();
    event.preventDefault();
    if (openedPoint.value?.id === point.id) {
      openedPoint.value = null;
    } else {
      if (!pointData.value[point.id]) {
        try {
          pointData.value[point.id] = await $api.$get(`/checkouts/${currentCheckoutType.value}/available_pickup_points/${point.type}/${point.id}`);
        } catch (error) {
          $logger.error(error);
        }
      }
      openedPoint.value = point;
    }
  }

  function handleCheckboxChange (courier) {
    if (!hasCheckedCheckboxes.value) {
      courier.checked = true;
      toasterStore.push({
        title: this.$awt('aw.map.sidebar.required_one_courier'),
        showModifyButton: false,
        modifyButtonCallback: () => ({}),
      });
    }

    emit('courier-checked-change', checkboxes.value.reduce((obj, item) => {
      obj[item.courier] = item.checked;
      return obj;
    }, {}));
  }

  function handleMobileLineClick () {
    emit('mobile-line-click');
  }

  watch(searchText, () => {
    emit('search-text-changed', searchText.value);
  });


</script>


<style module lang="scss" rel="stylesheet/scss">
.sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  &Top {
    display: grid;
    width: 100%;
    padding: 12px;
    border-radius: 16px;
    background: $color-background-4;
    gap: 12px;
    grid-template-columns: 1fr;

    .searchInput {
      --centeredButtonPosition: -36px;
      height: 40px;

      [data-search-input] {
        @include font($font-weight-normal-v2, 12px, 16px);
      }

      [data-search-input-filled] {
        @include font($font-weight-bold-v2, 12px, 16px);
      }
    }

    .mobileLine {
      width: 71px;
      height: 0;
      margin: -2px 0;
      border: 2px solid $color-border;
      border-radius: 4px;
      justify-self: center;
    }
  }

  &Empty {

    display: flex;
    align-items: center;
    flex-flow: column;
    justify-content: center;
    height: 100%;
    text-align: center;
    color: $color-text-secondary;
    gap: 16px;
    @include font($font-weight-regular-v2, 12px, 16px);

    .emptyImage {
      width: 96px;
      height: 96px;
    }
  }
}

.deliveryMethodMessages {
  padding-bottom: 8px;
}

.additionalTextInfoBoxes {
  display: flex;
  flex-direction: column;
  padding: 12px 12px 0;
  gap: 8px;
}

.point {
  display: grid;
  width: 100%;
  padding: 12px;
  cursor: pointer;
  border-radius: 12px;
  background: $color-background-4;
  gap: 8px;
  grid-template-areas: "logo price" "info info";
  grid-template-columns: 1fr max-content;

  &:hover {
    padding: 11px;
    border: 1px solid $color-border;
  }

  &List {
    display: contents;
  }

  &Wrapper {
    contain: layout style;
    display: grid;
    grid-template-columns: auto;
    gap: 4px;
    padding: 12px 12px 0;

    &Scrollable {
      overflow-y: scroll;
      max-height: 348px;
      padding: 12px 0 12px 12px;

      &.pointWrapperBig {
        max-height: 384px;
      }

      &::-webkit-scrollbar {
        width: 12px;
        height: 6px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 36px;
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        border: 3px solid transparent;
        border-radius: 36px;
        background-color: $color-border-disabled;
        background-clip: content-box;

        &:hover,
        &:active {
          background-color: $color-border-disabled;
        }
      }
    }

    &width3 {
      max-height: 436px;

      &.pointWrapperBig {
        max-height: 472px;
      }
    }
  }


  &Opened {
    grid-template-areas: "logo price" "info info" "openedInfo openedInfo";

    .pointAddress {
      white-space: normal;
    }
  }

  &Selected {
    padding: 10px !important;
    border: 2px solid $color-marigold-300 !important;
    background: $color-marigold-300--12;
  }

  &Info {
    display: grid;
    white-space: nowrap;
    grid-area: info;
    grid-template-columns: 1fr 16px;
    grid-template-areas: "title expand" "address expand";
    gap: 0 8px;
  }

  &Expand {
    grid-area: expand;
    align-self: flex-end;
  }

  &Logo {
    grid-area: logo;
  }

  &Price {
    grid-area: price;
    justify-self: right;
    font-size: 12px;
    line-height: 16px;
  }

  &Title {
    grid-area: title;
    @include font($font-weight-bold-v2, 12px, 16px);
    @include multiline-ellipsis(1);
    display: block !important;
  }

  &Address {
    grid-area: address;
    @include font($font-weight-normal-v2, 12px, 16px);
    @include multiline-ellipsis(1);
    display: block !important;
  }
}

.checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.courierLabel {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  @include font($font-weight-regular-v2, 12px, 16px);
}

.promotionalIcon {
  flex-shrink: 0;
}


.openedPointInformation {
  display: flex;
  flex-flow: column;
  gap: 8px;
  grid-area: openedInfo;
  @include font($font-weight-normal-v2, 12px, 16px);
}

.informationLabel {
  @include font($font-weight-bold-v2, null, null);
}

.informationList {
  display: flex;
  flex-flow: column;
  gap: 4px;
}

.informationHour {
  display: grid;
  gap: 8px;
  justify-content: space-between;
  grid-template-columns: max-content max-content;
}

.spinnerContainer {
  position: relative;
  flex-grow: 1;
}

.spinner {
  position: absolute;
  z-index: 1000;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: auto;
}
</style>
